import type { JSX } from "react";
import { cn } from "./cn";

export interface CalendarEventProps {
  /** Event title */
  readonly title: string;
  /** Time label */
  readonly time?: string;
  /** Color token: "blue", "green", "orange", "red" */
  readonly color: string;
  /** Extra className (for grid positioning) */
  readonly className?: string;
  /** Inline style (for grid placement) */
  readonly style?: React.CSSProperties;
  /** Display size */
  readonly size?: "sm" | "md";
}

const colorMap: Readonly<Record<string, string>> = {
  blue: "bg-blue text-white",
  green: "bg-green text-on-bright",
  orange: "bg-amber text-on-bright",
  red: "bg-red text-white",
};

const fallback = "bg-surface-hover text-content";

/**
 * A colored calendar event block. Supports both list and grid layouts.
 */
export function CalendarEvent({
  title,
  time,
  color,
  className,
  style,
  size = "md",
}: CalendarEventProps): JSX.Element {
  const colorClasses = colorMap[color] ?? fallback;

  if (size === "sm") {
    return (
      <div
        className={cn("rounded-md px-2 py-1 text-[10px] leading-tight", colorClasses, className)}
        style={style}
      >
        <div className="font-medium">{title}</div>
        {time ? <span className="opacity-80">{time}</span> : null}
      </div>
    );
  }

  return (
    <div
      className={cn("px-3.5 py-2.5 rounded-lg flex items-center justify-between text-sm", colorClasses, className)}
      style={style}
    >
      {title}
      {time ? <span className="text-xs opacity-80">{time}</span> : null}
    </div>
  );
}
